import config from "config"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

/**
 * Instruction from mui doc
 * ==============================
 * Ref: https://mui.com/material-ui/customization/theming/
 * Theme composition: using theme options to define other options
 * When the value for a theme option is dependent on another theme option, you should compose the theme in steps.
 */

export const venueKioskVUIThemeOptionFactory = (theme) => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          userSelect:
            config("env") === "production" &&
            ["kiosk-v", "kiosk-h"].includes(config("device"))
              ? "none"
              : "auto",
        },
        "@keyframes pulse": {
          "0%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.6,
          },
          "100%": {
            opacity: 1,
          },
        },
        "@keyframes ripple": {
          from: {
            opacity: 1,
            transform: "scale3d(0.75,0.75,1)",
          },
          to: {
            opacity: 0,
            transform: "scale3d(1.6,1.6,1)",
          },
        },
        "@keyframes slideUp": {
          from: {
            transform: "translateY(100%)",
          },
          to: {
            transform: "translateY(0%)",
          },
        },
        "@keyframes maptalksfadeIn": {
          from: {
            opacity: 0,
          },

          to: {
            opacity: 1,
          },
        },
        "@keyframes highlightTag": {
          "100%": {
            backgroundPosition: "-100% 0",
          },
        },
        ".mtk-occupant-text-marker": {},
        /**
         * "mtk-ui-fadein" is the build class name for handling UIComponent fade-in animation in maptalks.js.
         * [@Soruce](https://github.com/maptalks/maptalks.js/blob/2a4ab207619543237a2943bbb1c293863c6a98bd/src/ui/UIComponent.ts#L201)
         * [@Style](https://github.com/maptalks/maptalks.js/blob/2a4ab207619543237a2943bbb1c293863c6a98bd/assets/maptalks.css#L202)
         */
        ".mtk-ui-fadein": {
          animation: "maptalksfadeIn 0.5s",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
      variants: [
        {
          props: { variant: "logo" },
          style: {
            borderRadius: 8,
            width: 55,
            height: 55,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "contain",
            },
          },
        },
        {
          props: { variant: "circular-logo" },
          style: {
            borderRadius: "50%",
            width: 80,
            height: 80,
            marginRight: theme.spacing(2.25),
            "& .MuiAvatar-img": {
              objectFit: "cover",
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            background: theme.palette.primary.main,
            borderRadius: 5,
            "& .MuiSvgIcon-root": {
              width: 20,
            },
          },
        },
      ],
    },
    MuiCard: {
      defaultProps: {
        elevation: 15,
      },
      styleOverrides: {
        root: {
          borderRadius: "0",
          color: "#000000",
        },
      },
      variants: [
        {
          props: { variant: "image" },
          style: {
            borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
            "& .MuiCardActionArea-root:after": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.84))",
            },
            "& .MuiCardMedia-root": {
              height: "140px",
            },
            "& .MuiCardHeader-root": {
              position: "absolute",
              padding: theme.spacing(0, 1),
              minHeight: "60px",
              width: "100%",
              bottom: "0px",
              zIndex: 1,
              "& .MuiCardHeader-content": {
                color: "white",
              },
              "& .MuiCardHeader-title": {
                lineHeight: 1,
              },
              "& .MuiAvatar-root": {
                borderRadius: `${(theme.shape.borderRadius * 2) / 3}px`,
                width: "45px",
                height: "45px",
              },
              "& .MuiCardHeader-avatar": {
                marginRight: theme.spacing(1),
              },
              "& .MuiCardHeader-subheader": {
                fontSize: "0.75rem",
                color: "white",
              },
            },
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
          borderRadius: "8px",
          boxShadow: "none",
        },
        startIcon: {
          marginRight: theme.spacing(1.25),
        },
        endIcon: {
          marginLeft: theme.spacing(1.25),
        },
        sizeSmall: {
          fontSize: 20,
          padding: theme.spacing(1, 2.5),
        },
        sizeMedium: {
          fontSize: 22,
          padding: theme.spacing(1.5, 4),
        },
        sizeLarge: {
          fontSize: 24,
          padding: theme.spacing(2, 5),
        },
        outlinedSizeLarge: {
          borderWidth: "2px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderBottomWidth: theme.spacing(0.25),
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          whiteSpace: "pre",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
      variants: [
        {
          props: { variant: "medium" },
          style: {
            "& .MuiListItemText-primary": {
              fontSize: theme.typography.body2.fontSize,
            },
            "& .MuiListItemText-secondary": {
              fontSize: theme.typography.caption.fontSize,
            },
          },
        },
        {
          props: { color: "primary" },
          style: {
            "& .MuiListItemText-primary": {
              color: theme.palette.primary.main,
            },
          },
        },
      ],
    },
    MuiRadio: {
      defaultProps: {
        icon: <CheckCircleIcon />,
        checkedIcon: <CheckCircleIcon />,
      },
    },
    MuiFab: {
      styleOverrides: {
        default: {
          background: "#fff",
        },
        sizeLarge: {
          padding: theme.spacing(5, 6),
          "& .MuiSvgIcon-root": {
            width: 40,
            height: 40,
          },
        },
      },
      variants: [
        {
          props: { variant: "rounded" },
          style: {
            borderRadius: 12,
          },
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            background: theme.palette.primary.dark,
          },
          "& .MuiSvgIcon-root": {
            "& path": { fill: theme.palette.primary.contrastText },
          },
        },
      },
      variants: [
        {
          props: { variant: "contained" },
          style: {
            borderRadius: 5,
          },
        },
      ],
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { gap: theme.spacing(1.25), paddingRight: theme.spacing(2) },
        grouped: {
          "&:not(:last-of-type)": {
            borderRadius: (theme.shape.borderRadius * 2) / 3,
          },
          "&:not(:first-of-type)": {
            marginLeft: 1,
            borderLeft: `1px solid rgba(203, 203, 203, 0.5)`,
            borderRadius: (theme.shape.borderRadius * 2) / 3,
          },
        },
      },
      variants: [
        {
          props: { variant: "borderSeperate" },
          style: {
            "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
              borderLeft: `1px solid transparent`,
            },
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: (theme.shape.borderRadius * 2) / 3,
          color: theme.palette.primary.main,
          borderColor: "rgba(203, 203, 203, 0.5)",
          "&.Mui-selected, &.Mui-selected:hover": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: theme.spacing(2),
        },
      },
    },
    /**
     * VenueUI (Vui) - Vertical Kiosk
     * Extra Component for venue.in.th projects
     */
    VuiDescriptionList: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        labelText: {
          fontWeight: 700,
          fontSize: "12px",
          textTransform: "uppercase",
        },
        contentText: {
          textAlign: "center",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          wordBreak: "break-all",
          fontWeight: 300,
        },
      },
    },
    VuiOnscreenKeyboard: {
      styleOverrides: {
        root: {
          // [Example] (https://hodgef.com/simple-keyboard/editor/?d=hodgef/react-simple-keyboard-demos/tree/uc-customization)
          // Add your custom style here
          "& .hg-theme-default": {
            fontFamily: "inherit",
          },
          "& .simple-keyboard": {
            maxWidth: "100%",
            padding: theme.spacing(8, 11.75),
            borderRadius: "20px",
            backgroundColor: theme.palette.grey[200],
            "&.hg-theme-default .hg-row": {
              marginBottom: theme.spacing(1.75),
              "&:last-child": {
                marginBottom: 0,
              },
              "& .hg-button:not(:last-child)": {
                marginRight: theme.spacing(1.75),
              },
            },
            "& .hg-button": {
              height: 65,
              backgroundColor: "#333333",
              color: theme.palette.common.white,
              fontSize: "32px",
              fontWeight: 500,
              borderRadius: "8px",
              "&>span": {
                display: "flex",
                fontSize: "inherit",
              },
            },
            "& .hg-activeButton": {
              backgroundColor: theme.palette.grey[300],
              color: theme.palette.text.primary,
            },
            "& .hg-button-number,& .hg-button-symbol, & .hg-button-abc,& .hg-button-lang":
              {
                fontSize: "12px",
                flexGrow: 1,
              },
            "& .hg-button-lang, & .hg-button-number, & .hg-button-abc": {
              flexGrow: 0.5,
            },
            "& .hg-button-shift,& .hg-button-bksp": {
              fontSize: "20px",
            },
            "& .hg-button-shift": {
              fontSize: "20px",
              flexGrow: 2,
              flexShrink: 0,
            },
            "& .hg-button-enter": {
              flexGrow: 2,
            },
            "& .hg-button-space": {
              flexGrow: 8,
            },
            "& .hg-primary-button": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            "& .hg-secondary-button": {},
            "& .hg-small-button": {},
          },
        },
      },
    },
    VuiSearchToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: `9999px`,
          background: theme.palette.background.paper,
          width: "100%",
          padding: theme.spacing(1, 2.5),
          justifyContent: "flex-start",
          border: `1px solid #ececec`,
        },
        searchIcon: {
          color: theme.palette.common.black,
          fontSize: "1.125rem",
          marginRight: theme.spacing(2.5),
        },
        label: {
          flexGrow: "1",
          textAlign: "start",
          color: theme.palette.text.secondary,
        },
      },
    },
    VuiSearchBasePopup: {
      styleOverrides: {
        drawer: {
          width: 350,
          display: "flex",
          flexDirection: "column",
        },
        drawerPaper: {
          overflow: "hidden",
          width: "100%",
          [theme.breakpoints.up("sm")]: {
            width: 400,
          },
        },
        dialog: {
          "& .MuiDialogContent-root": {
            [theme.breakpoints.down("sm")]: {
              padding: theme.spacing(1.25, 2),
            },
          },
        },
        dialogPaper: {
          alignSelf: "flex-end",
          margin: theme.spacing(4, 6.25),
        },
        dialogContent: {
          display: "flex",
          padding: theme.spacing(5),
          flexDirection: "column",
          position: "relative",
          // Apply a className to each section for ordering.
          "& .VuiSearchResult-root": {
            order: 0,
          },
          "& .VuiSearchPopupContent-searchBar": {
            order: 2,
          },
          "& .VuiSearchKeyboard-root": {
            order: 9999,
          },
          "& .VuiSearchPopupContent-closeButton": {
            zIndex: 9999,
            position: "absolute",
            top: theme.spacing(1.5),
            right: theme.spacing(1.5),
          },
          "& .VuiSearchPopupContent-closeButton--bottom": {
            display: "none",
          },
          "& .VuiSearchPopupContent-venueFilterContainer": {
            display: "none",
          },
        },
        dialogBackdrop: {
          top: 0,
          bottom: 0,
        },
      },
    },
    VuiSearchPopupContent: {
      styleOverrides: {
        root: {
          display: "flex",
          padding: theme.spacing(3.125, 3.125, 1.5, 3.125),
          width: "auto",
          gap: theme.spacing(2.5),
          alignItems: "center",
          color: theme.palette.text.primary,
          backgroundColor: "transparent",
        },
        closeButton: {
          padding: 0,
          width: "32px",
          height: "32px",
          "& .MuiSvgIcon-root": {
            width: "12px",
            height: "12px",
          },
        },
        closeButtonIcon: {
          color: "inherit",
        },
        suggestedList: {},
        searchInput: {
          backgroundColor: theme.palette.common.white,
          borderRadius: 9999,
          padding: theme.spacing(1.25, 2),
          fontSize: "1rem",
          gap: theme.spacing(2),
          fontWeight: 700,
          "& input": {
            padding: 0,
          },
          "& .MuiSvgIcon-root": {
            fontSize: "1.125rem",
            color: theme.palette.text.primary,
          },
          "& .MuiInputAdornment-root": {
            "&.MuiInputAdornment-positionEnd": {
              "& .MuiSvgIcon-root": {
                width: 8,
                height: 8,
              },
            },
          },
        },
      },
    },
    VuiAdSuggestList: {
      defaultProps: {
        suggestBrandCarouselOptions: {
          perPage: 7,
          gap: "0.75rem",
        },
      },
      styleOverrides: {
        root: {
          padding: theme.spacing(1.5),
          backgroundColor: "#F2F2F2",
          marginBottom: theme.spacing(3),
          borderRadius: "8px",
        },
        suggestTitle: {
          fontSize: "1.25rem",
          fontWeight: 700,
        },
        suggestList: {
          "& .splide__track": {
            margin: 0,
            padding: theme.spacing(2, 0, 0.5),
          },
        },
        suggestListItem: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          gap: theme.spacing(1),
          justifyContent: "center",
          alignItems: "center",
        },
        suggestItemAvatar: {
          width: "110px",
          height: "110px",
          margin: 0,
          "& .MuiSvgIcon-root": {
            width: "40px",
            height: "40px",
          },
        },
        suggestItemContent: {
          marginTop: theme.spacing(0.5),
          height: "40px",
          "& .MuiTypography-root": {
            fontSize: "1rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textAlign: "center",
          },
        },
      },
    },
    VuiSearchQuickLink: {
      styleOverrides: {
        root: {
          pb: 1,
          mb: 1,
          mx: -2,
          px: 2,
        },
        stack: {
          pb: 2,
        },
        item: {
          boxShadow: "rgb(0 0 0 / 10%) 0px 2px 10px",
          border: "none",
          background: "white",
          "& .MuiSvgIcon-root": {
            width: "16px",
            height: "16px",
          },
        },
      },
    },
    VuiSearchResult: {
      styleOverrides: {
        root: {
          flexGrow: 1,
          width: "100%",
          marginTop: theme.spacing(6),
          "&:after": {
            display: "flex",
            pointerEvents: "none",
            content: "''",
            width: "100%",
            height: "147px",
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -2,
            background: `linear-gradient(0deg, ${theme.palette.background.paper} 9.97%, transparent 72.39%)`,
          },
        },
        rootDialog: {
          height: "560px",
          padding: theme.spacing(7.5, 5, 3, 5),
          position: "relative",
        },
        list: {
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gridGap: theme.spacing(1.5),
          paddingTop: 0,
          paddingBottom: theme.spacing(11.25),
        },
        listItem: {
          backgroundColor: theme.palette.grey[200],
          height: "88px",
        },
      },
    },
    VuiSearchResultItem: {
      styleOverrides: {
        chip: {
          borderRadius: "4px",
          height: "18px",
          fontWeight: 400,
          backgroundColor: theme.palette.grey[400],
          color: theme.palette.common.white,
          fontSize: "0.625rem",
          "& .MuiChip-label": {
            padding: theme.spacing(0, 1),
          },
        },
        primaryWrapper: {
          display: "flex",
          alignItems: "flex-start",
          gap: theme.spacing(0.75),
        },
      },
    },
    VuiNoSearchResult: {
      styleOverrides: {
        root: {
          display: "flex",
          height: "300px",
          justifyContent: "center",
          alignItems: "center",
        },
        noResultText: {},
      },
    },
    VuiSearchKeyboard: {
      styleOverrides: {
        root: {},
      },
    },
    VuiDetailCard: {
      styleOverrides: {
        card: {
          height: "100%",
          display: "grid",
          width: "100%",
          gridTemplateAreas:
            "'img title' \n 'img info' \n 'img content' \n 'qr content' \n 'btn btn'",
          gridTemplateRows: "70px 152px 386px 143px 80px",
          gridTemplateColumns: "530px 395px",
          background: "transparent",
          padding: theme.spacing(5, 0, 0, 0),
          borderRadius: "32px",
          gap: theme.spacing(2, 4),
        },
        infoListItem: {
          "& .MuiListItemText-root .MuiListItemText-primary": {
            color: "#494656",
            fontSize: "1.5rem",
            fontWeight: 700,
            lineHeight: 1,
            "& .MuiTypography-root": {
              fontSize: "1.5rem",
              fontWeight: 700,
              lineHeight: 1,
            },
          },
          "& .MuiListItemText-secondary": {
            fontSize: "1rem",
            color: "#8C8A94",
            marginTop: theme.spacing(1),
            lineHeight: 1,
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            maxWidth: "fit-content",
            wordBreak: "break-word",
            textWrap: "auto",
          },
          "& .MuiAvatar-root": {
            background: "#C6C5C9",
            borderRadius: "8px",
            width: 56,
            height: 56,
            marginRight: theme.spacing(2),
            "& .MuiSvgIcon-root": {
              width: "auto",
              fontSize: "2rem",
            },
          },
          "& .MuiListItemAvatar-root": {
            minWidth: "64px", // 48px (MuiAvatar-root) + 16px (spacing)
          },

          overflow: "hidden",
          maxWidth: "90%",
          "& .MuiTypography-root": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          },
          "&.MuiListItem-root:last-child .MuiListItemText-root .MuiListItemText-secondary":
            {
              display: "none",
            },
        },
        cardHeader: {
          "& .MuiCardHeader-title": {
            fontWeight: 700,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            fontSize: "1.75rem",
            lineHeight: 1.4,
            wordBreak: "break-word",
          },
          "& .MuiCardHeader-action": {
            marginRight: 0,
          },

          gridArea: "title",
          color: "#494656",
          textAlign: "left",
          padding: 0,
          alignItems: "flex-start",
          paddingBottom: 0,
        },
        cardContent: {
          padding: 0,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          gridArea: "content",
          marginTop: 0,
          "::after": {
            content: "''",
            position: "absolute",
            bottom: "0",
            left: "0",
            width: "100%",
            height: "80px",
            background:
              "linear-gradient(to bottom, rgba(248, 248, 255, 0) 0%, #f8f8ff 100%)",
            pointerEvents: "none",
          },
        },
        cardContentPrimaryText: {
          marginBottom: theme.spacing(1),
          fontWeight: 700,
          color: "#494656",
          fontSize: "1.25rem",
          marginTop: theme.spacing(5),
        },
        cardContentSecondaryText: {
          fontSize: "1rem",
          color: "#8C8A94",
          overflowY: "auto",
          lineHeight: "1.4",
          width: "380px",
          height: "450px",
          minHeight: "450px",
          paddingRight: theme.spacing(2),
          whiteSpace: "normal",
          wordWrap: "break-word",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "::-webkit-scrollbar-track": {
            borderRadius: "8px",
            background: theme.palette.grey[300],
          },
          "::-webkit-scrollbar-thumb": {
            borderRadius: "8px",
            background: theme.palette.primary.main,
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.primary.main,
          },
        },
        divider: {
          display: "none",
        },
        infoContainer: {
          display: "grid",
          gridGap: theme.spacing(2),
          gridArea: "info",
          gridTemplateRows: "62px 62px",
          marginTop: "24px",
          padding: 0,
          gridTemplateColumns: "1fr",
        },
        infoDivider: {
          "&:first-child": {
            display: "none",
          },
        },
        qrCode: {
          marginTop: 0,
          borderRadius: "4px",
          boxShadow: "none",
          border: "none",
          padding: theme.spacing(1.5),
          width: "108px",
          height: "108px",
          ":before": {
            display: "none",
          },
          ":after": {
            display: "none",
          },
          "& .MuiCardContent-root:last-child": {
            paddingBottom: 0,
          },
        },
        qrCodeContainer: {
          width: "100%",
          textAlign: "center",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gridArea: "qr",
          backgroundColor: theme.palette.common.white,
          border: "1px solid #E2E2E4",
          borderRadius: "24px",
          padding: theme.spacing(1.5),
          gap: theme.spacing(2),
          marginTop: theme.spacing(2),
        },
        qrcodePrimaryText: {
          fontWeight: 700,
          fontSize: "1.875rem",
          color: "#494656",
          textAlign: "left",
          lineHeight: 1.1,
        },
        qrcodeSecondaryText: {
          fontSize: "1.875rem",
          color: "#8C8A94",
          textAlign: "left",
          lineHeight: 1.1,
        },
        buttonContainer: {
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gridArea: "btn",
          gap: theme.spacing(3),
          marginTop: theme.spacing(3),
          height: "80px",
        },
        button: {
          flex: "0 0 188px",
          textTransform: "capitalize",
          padding: theme.spacing(1.5, 3),
          borderRadius: "32px",
          fontSize: "1.25rem",
          height: "80px",
          minWidth: "200px",
          fontWeight: 400,
          "&.MuiButton-outlined": {
            border: "1px solid #B3B1B8",
            color: "#8C8A94",
            backgroundColor: theme.palette.common.white,
          },
        },
        cardMediaWrapper: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: theme.spacing(3),
          gridArea: "img",
          height: "660px",
        },
        galleryCarouselWrapper: {
          margin: "0 auto",
          width: "100%",
        },
        galleryCarousel: {
          "& .splide__track": {
            padding: 0,
            margin: 0,
          },
          "& .splide__pagination": {
            position: "absolute",
          },
          "& .splide__pagination__page": {
            width: "14px",
            height: "14px",
          },
          "& .splide__pagination__page.is-active": {
            backgroundColor: theme.palette.primary.main,
            transition: `width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, borderRadius ${theme.transitions.duration.standard}s ${theme.transitions.easing.easeInOut}`,
            transform: "none",
            width: 45,
            borderRadius: 30,
          },
        },
        galleryCarouselItem: {
          objectFit: "cover",
          borderRadius: "24px",
          height: "660px",
          maxWidth: "525px",
          maxHeight: "660px",
          width: "525px",
        },
        navigationWrapper: {
          zIndex: theme.zIndex.appBar,
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: theme.spacing(0, 8),
          gap: theme.spacing(3),
        },
        actionButton: {
          display: "none",
          minWidth: 128,
          backgroundColor: theme.palette.common.white,
          color: theme.palette.text.primary,
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          "& .MuiSvgIcon-root": {
            marginRight: 0,
            width: 32,
            height: 32,
          },
        },
        actionContainer: {
          display: "flex",
          flexDirection: "row",
          gap: theme.spacing(1.25),
        },
        toolbarContainer: {
          position: "relative",
          display: "flex",
          gap: theme.spacing(1.25),
          flexDirection: "row-reverse",
        },
        searchBarContainer: {
          width: "100%",
          height: 48,
          display: "flex",
        },
      },
    },
    VuiAdModal: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        },
        closeButton: {
          padding: theme.spacing(0.5, 2),
          marginTop: theme.spacing(2),
        },
      },
    },
    VuiAdImageCarousel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        carousel: {
          "& .splide__pagination": {
            marginTop: theme.spacing(1),
          },
          "& .splide__pagination__page": {
            width: "14px",
            height: "14px",
          },
          "& .splide__pagination__page.is-active": {
            backgroundColor: theme.palette.primary.main,
            transition: `width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}, borderRadius ${theme.transitions.duration.standard}s ${theme.transitions.easing.easeInOut}`,
            transform: "none",
            width: 45,
            borderRadius: 30,
          },
        },
        carouselItem: {
          display: "flex",
          justifyContent: "center",
        },
        image: {
          width: "720px",
          aspectRatio: "3 / 4",
          objectFit: "cover",
          borderRadius: "16px",
        },
      },
    },
    VuiAdMediaCarouselScreensaver: {
      styleOverrides: {
        carouselItem: {
          height: "1250px",
        },
      },
    },
    VuiAdMediaCarouselFooter: {
      styleOverrides: {
        root: {
          height: "100%",
          width: "100%",
        },
        carousel: {
          "& .splide__track": {
            padding: 0,
            margin: 0,
          },
        },
        carouselItem: {
          width: "100%",
          height: theme?.mixins?.vKioskFooter?.height || 300,
          objectFit: "cover",
        },
      },
    },
    VuiCarousel: {
      styleOverrides: {
        root: {
          "& .splide__pagination__page": {
            background: theme.palette.grey[500],
          },
          "& .splide__pagination__page.is-active": {
            background: theme.palette.primary.main,
          },
          "& .splide__pagination": {
            position: "static",
          },
          "& .splide__track": {
            overflow: "hidden",
            margin: theme.spacing(-3),
            padding: theme.spacing(3),
          },
          "& .splide__arrow": {
            background: "none",
            margin: theme.spacing(0, -5),
            "& svg": {
              fill: theme.palette.grey[500],
            },
          },
          "& .splide:not(.is-overflow) .splide__pagination": {
            display: "flex",
            visibility: "hidden",
          },
          "& .splide:not(.is-overflow):not(:has(li:only-child)) .splide__pagination":
            {
              visibility: "visible", // pagination is visible when there's more than 1 slide in carousel
            },
        },
      },
    },
    HorizontalScrollPane: {
      styleOverrides: {
        root: {
          width: "100%",
          "& .simplebar-track.simplebar-horizontal": {
            display: "none",
          },
        },
        listWrapper: {
          width: "100%",
          padding: theme.spacing(1),
        },
      },
    },
    LoadingScreen: {
      styleOverrides: {
        root: {
          position: "fixed",
          width: "100dvw",
          height: "100dvh",
          backgroundColor: theme.palette.common.white,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
        },
        logo: {
          maxWidth: "70dvw",
        },
      },
    },
    FeatureChipToggleBar: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: 0,
        },
        button: {
          padding: theme.spacing(0.5),
          borderRadius: "16px",
          boxShadow: "none",
          background: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          whiteSpace: "nowrap",
          height: "32px",
          color: theme.palette.text.primary,
          fontWeight: 400,
          "&.MuiToggleButtonGroup-grouped:not(:last-of-type)": {
            borderRadius: "16px",
            margin: 0,
          },
          "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
            border: `1px solid ${theme.palette.grey[200]}`,
            margin: 0,
            borderRadius: "16px",
          },
          "&.MuiToggleButtonGroup-grouped:first-of-type": {
            marginLeft: theme.spacing(6.25),
          },
          "&.MuiToggleButtonGroup-grouped:last-of-type": {
            marginRight: theme.spacing(6.25),
          },
          "&.Mui-selected": {
            "& svg": {
              fill: theme.palette.primary.contrastText,
            },
          },
          ":hover": {
            background: theme.palette.common.white,
          },
          "& svg": {
            width: 16,
            height: 16,
            fill: theme.palette.primary.main,
            marginLeft: theme.spacing(0.5),
            marginRight: "-6px",
          },
        },
        text: {
          fontSize: "0.8125rem",
          padding: theme.spacing(0, 1.5),
          letterSpacing: "0.00938em",
        },
      },
    },
    VuiAnnoucementCard: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
        },
        closeButtonContainer: {
          display: "flex",
          justifyContent: "flex-end",
        },
        closeButton: {
          backgroundColor: "rgba(255, 255, 255, 0.50)",
          borderRadius: "50%",
          padding: 0,
          minWidth: "24px",
          "& .MuiSvgIcon-root": {
            width: "50%",
          },
          cursor: "pointer",
          "&.MuiButtonBase-root:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.50)",
          },
        },
        detailCard: {
          display: "flex",
          padding: theme.spacing(2),
          gap: theme.spacing(1),
          borderRadius: "16px",
        },
        cardContent: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          "&.MuiCardContent-root:last-child": {
            paddingBottom: 0,
          },
        },
        cardMedia: {
          aspectRatio: "1/1",
          width: "30%",
          borderRadius: "8px",
          objectFit: "cover",
        },
        titleContainer: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          color: theme.palette.primary.main,
        },
        locationContainer: {
          display: "flex",
          gap: theme.spacing(1),
        },
        iconWrapper: {
          width: "16px",
          height: "16px",
          "& .MuiSvgIcon-root": {
            height: "100%",
            width: "100%",
          },
        },
      },
    },
  },
})
